/**
 * The physician's care-model tasks on the patient record: the patient's ACTIVE care-model runs, and for each run the
 * Checklist step it is waiting on, with its items and why the run reached it (ELLMS, 23 Oct 2026: the care model is
 * infrastructure; the physician completes its review tasks on the physician side, not in the studio).
 *
 * The reads are the ones pm-react's studio run view makes (`src/studio/services/care-model-run-api.ts`), on the same
 * Scrips.Workflows host. This module is the typed boundary over their bodies: every parser takes `unknown` and fails
 * closed. Pure; no React, no fetch.
 *
 * WHAT THE ENGINE TELLS US (Scrips.Workflows `ScripsChecklistActivity`, PROD-1005 D4), as the studio reads it:
 *   · a Checklist step is WAITING when its last log record is Executed/Resumed with no outcome and the run is Suspended;
 *     only then can a tick move it.
 *   · the item labels are the step's `Items` input, verbatim: the engine matches a tick on the exact label.
 *   · there is no read of one item's state (`activityData[step].CompletedItems` is the authored seed, not updated by a
 *     tick). So an item reads done only when it was ticked from this browser (kept per run and step, as the studio does).
 *   · a step assigned to named people may be ticked only by one of them; an unassigned step by anyone in the practice.
 * The "why" is read from the run, never inferred: the step the run executed just before the checklist and, for a
 * calculation that gave no score, the engine's reason in clinician words (pm-react #1339) and the inputs the engine
 * itself names as missing.
 */

import { CALC_FAULT, calcReasonVariable, isCalcFault, notCalculatedText } from './calc-not-computed-reason';

type Json = Record<string, unknown>;

const isObject = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

export const CHECKLIST_ACTIVITY_TYPE = 'ScripsChecklistActivity';
export const CALCULATION_ACTIVITY_TYPE = 'ScripsCalculationActivity';

/* ── Run reads ────────────────────────────────────────────────────────────── */

/** GET /workflows/care-models/enrolments/{id}/run → the instance id (`{ data: { workflowInstanceId } }`). */
export function parseRunPointer(body: unknown): string | null {
  const d = isObject(body) && 'data' in body ? body.data : body;
  return isObject(d) ? str(d.workflowInstanceId) : null;
}

export interface RunInstance {
  id: string;
  definitionVersionId: string;
  /** Elsa's word: Running | Suspended | Finished | Cancelled | Faulted | Idle. */
  workflowStatus: string;
  variables: Readonly<Json>;
  activityData: Readonly<Record<string, Readonly<Json>>>;
}

/** GET /v1/workflow-instances/{id}. */
export function parseRunInstance(body: unknown): RunInstance | null {
  if (!isObject(body)) return null;
  const id = str(body.id);
  const definitionVersionId = str(body.definitionVersionId);
  const workflowStatus = str(body.workflowStatus);
  const vars = isObject(body.variables) ? body.variables.data : undefined;
  if (!id || !definitionVersionId || !workflowStatus || !isObject(vars) || !isObject(body.activityData)) return null;
  const activityData: Record<string, Json> = {};
  for (const [key, value] of Object.entries(body.activityData)) if (isObject(value)) activityData[key] = value;
  return { id, definitionVersionId, workflowStatus, variables: vars, activityData };
}

export interface RunLogEvent {
  activityId: string;
  eventName: string;
  timestamp: string;
  outcomes: readonly string[];
}

/** GET /v1/workflow-instances/{id}/execution-log, in time order. An unreadable record makes the log unreadable. */
export function parseExecutionLog(body: unknown): RunLogEvent[] | null {
  const items = isObject(body) ? body.items : undefined;
  if (!Array.isArray(items)) return null;
  const events: RunLogEvent[] = [];
  for (const raw of items) {
    if (!isObject(raw)) return null;
    const activityId = str(raw.activityId);
    const eventName = str(raw.eventName);
    const timestamp = str(raw.timestamp);
    if (!activityId || !eventName || !timestamp || Number.isNaN(Date.parse(timestamp))) return null;
    events.push({ activityId, eventName, timestamp, outcomes: strings(isObject(raw.data) ? raw.data.Outcomes : undefined) });
  }
  return events
    .map((e, i) => ({ e, i }))
    .sort((a, b) => Date.parse(a.e.timestamp) - Date.parse(b.e.timestamp) || a.i - b.i)
    .map(({ e }) => e);
}

export interface RunActivity {
  activityId: string;
  type: string;
  /** The step title the author typed in the studio. */
  title: string;
  /** A checklist step's item labels, verbatim; null when the stored list cannot be read; absent on other steps. */
  items?: readonly string[] | null;
  /** A checklist step's explicit assignee ids (empty = no one assigned; then anyone in the practice may tick). */
  assigneeIds?: readonly string[] | null;
}

export interface RunDefinition {
  name: string | null;
  activities: readonly RunActivity[];
}

/**
 * The active expression of a property: `expressions[syntax ?? 'Literal']`, the one Elsa binds. An inactive entry left
 * under another syntax is never read (it is not what the run uses).
 */
function expressionOf(prop: Json): string | null {
  const ex = isObject(prop.expressions) ? prop.expressions : null;
  if (!ex) return null;
  const syntax = typeof prop.syntax === 'string' && prop.syntax.trim() !== '' ? prop.syntax : 'Literal';
  const value = ex[syntax];
  return typeof value === 'string' ? value : null;
}

/** `Items` → the labels exactly as stored. Missing = no items; a value that is not a list of strings = unreadable. */
export function readChecklistItems(properties: unknown): readonly string[] | null {
  const props = Array.isArray(properties) ? properties : [];
  const prop = props.find((p): p is Json => isObject(p) && p.name === 'Items');
  if (!prop) return [];
  const text = expressionOf(prop);
  // Values stored only under another syntax: the active one is missing, so the list is unknown, not empty.
  if (text === null) return isObject(prop.expressions) && Object.keys(prop.expressions).length > 0 ? null : [];
  if (text.trim() === '') return [];
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  return Array.isArray(value) && value.every((v) => typeof v === 'string') ? value : null;
}

function parseText(text: string | null): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** Trimmed, de-duplicated ids; null when a present field is not a string or a list of strings (fail closed). */
const idList = (v: unknown): string[] | null => {
  if (v === undefined || v === null) return [];
  const raw = typeof v === 'string' ? [v] : Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : null;
  if (raw === null) return null;
  return [...new Set(raw.map((x) => x.trim()).filter((x) => x !== ''))];
};

/** A stored text that is present but cannot be parsed: the assignment is UNKNOWN, not "no one". */
const UNREADABLE = Symbol('unreadable');
function parseStored(prop: Json | undefined): unknown {
  if (!prop) return undefined;
  const text = expressionOf(prop);
  // Present, but its value sits only under another syntax: the active value is unknown, not empty.
  if (text === null) return isObject(prop.expressions) && Object.keys(prop.expressions).length > 0 ? UNREADABLE : undefined;
  if (text.trim() === '') return undefined;
  const value = parseText(text);
  return value === undefined ? UNREADABLE : value;
}

/**
 * The step's explicit assignee ids, as the studio stores them (pm-react `run-checklist-read.ts` `readAssignees`): the
 * canvas blob's `assignedStakeholderIds`, else its legacy `assignedStakeholderId`, else the `scrips.studio.assignees`
 * carry. The canvas `stakeholders` stack is a display list, not an assignment, so it is not read.
 * `null` = the stored assignment cannot be read; the caller must then treat the step as read-only (fail closed).
 */
export function readAssigneeIds(properties: unknown): string[] | null {
  const props = Array.isArray(properties) ? properties : [];
  const prop = (name: string) => props.find((p): p is Json => isObject(p) && p.name === name);
  const canvas = parseStored(prop('scrips.studio.canvas'));
  if (canvas === UNREADABLE) return null;
  if (canvas !== undefined && !isObject(canvas)) return null;
  if (isObject(canvas)) {
    const ids = idList(canvas.assignedStakeholderIds);
    const one = idList(canvas.assignedStakeholderId);
    if (ids === null || one === null) return null;
    if (ids.length > 0) return ids;
    if (one.length > 0) return one;
  }
  const carry = parseStored(prop('scrips.studio.assignees'));
  if (carry === UNREADABLE) return null;
  return idList(carry);
}

/** GET /v1/workflow-definitions/{versionId}. */
export function parseRunDefinition(body: unknown): RunDefinition | null {
  if (!isObject(body) || !Array.isArray(body.activities)) return null;
  const activities: RunActivity[] = [];
  for (const a of body.activities) {
    if (!isObject(a)) return null;
    const activityId = str(a.activityId);
    if (!activityId) return null;
    const type = str(a.type) ?? '';
    activities.push({
      activityId,
      type,
      title: typeof a.displayName === 'string' ? a.displayName.trim() : '',
      ...(type === CHECKLIST_ACTIVITY_TYPE ? { items: readChecklistItems(a.properties), assigneeIds: readAssigneeIds(a.properties) } : {}),
    });
  }
  return { name: str(body.displayName) ?? str(body.name), activities };
}

/* ── The waiting checklist ────────────────────────────────────────────────── */

export interface ChecklistReason {
  /** The title of the step the run executed just before the checklist. */
  afterStep: string;
  /**
   * What that step did, for the physician:
   *   'not-calculated' — a calculation that gave no score (a clinical refusal or no reason on the run): neutral;
   *   'fault'          — a calculation that could not run (engine `configuration` reason, or the step faulted);
   *   'after'          — any other step: the run simply came here after it.
   */
  kind: 'not-calculated' | 'fault' | 'after';
  /** For a calculation: "Not calculated: <reason>", "Not calculated — see inputs", or the fault line. */
  text: string | null;
  /** The inputs the ENGINE names as missing (`MissingInputs`). Never inferred from the run's variables. */
  missingInputs: readonly string[];
}

export interface WaitingChecklist {
  /** Needed by the tick call; never rendered. */
  instanceId: string;
  activityId: string;
  title: string;
  /** Verbatim labels; null when the stored list could not be read. */
  items: readonly string[] | null;
  /** Explicit assignee ids; empty when no one is assigned; null when the stored assignment cannot be read. */
  assigneeIds: readonly string[] | null;
  reason: ChecklistReason | null;
}

const lastEvent = (log: readonly RunLogEvent[], activityId: string) => [...log].reverse().find((e) => e.activityId === activityId);

/** True while the run is suspended on this step: its last record is Executed/Resumed with no outcome. */
export function isWaiting(instance: RunInstance, log: readonly RunLogEvent[], activityId: string): boolean {
  if (instance.workflowStatus.trim().toLowerCase() !== 'suspended') return false;
  const last = lastEvent(log, activityId);
  return !!last && (last.eventName === 'Executed' || last.eventName === 'Resumed') && last.outcomes.length === 0;
}

/** "totalChol" → "total chol"; the common clinical abbreviations keep their usual case. */
export function inputWords(key: string): string {
  const ABBR: Record<string, string> = { hdl: 'HDL', ldl: 'LDL', sbp: 'SBP', dbp: 'DBP', egfr: 'eGFR', bmi: 'BMI', bp: 'BP', cvd: 'CVD' };
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => ABBR[w.toLowerCase()] ?? w.toLowerCase())
    .join(' ');
}

const hasAnswer = (v: unknown) => v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === '');

/**
 * Why the run reached this step: the step it executed just before, read from the log. For a calculation that gave no
 * score, the line is the engine's own reason (`calc.<engineId>.reason`, Scrips.Workflows #192) in clinician words; the
 * logged outcome ("Error") is never shown, because a refusal to score is a clinical result, not a fault. Missing inputs
 * are listed only when the engine names them: the run's variables hold only the answers the form step copied in, so a
 * bound input absent from them may well have been answered (seen on the dev test run, 23 Oct 2026).
 */
export function reasonFor(instance: RunInstance, log: readonly RunLogEvent[], definition: RunDefinition, activityId: string): ChecklistReason | null {
  const first = log.findIndex((e) => e.activityId === activityId);
  if (first <= 0) return null;
  const before = [...log.slice(0, first)].reverse().find((e) => e.activityId !== activityId);
  if (!before) return null;
  const act = definition.activities.find((a) => a.activityId === before.activityId);
  if (!act || !act.title) return null;
  const data = instance.activityData[act.activityId];
  if (act.type !== CALCULATION_ACTIVITY_TYPE || !data || hasAnswer(data.Value)) {
    return { afterStep: act.title, kind: 'after', text: null, missingInputs: [] };
  }
  const engineId = str(data.CalculatorId) ?? str(data['calc-engine-id']);
  const reason = engineId ? instance.variables[calcReasonVariable(engineId)] : undefined;
  const faulted = lastEvent(log.slice(0, first), act.activityId)?.eventName === 'Faulted' || isCalcFault(reason);
  return {
    afterStep: act.title,
    kind: faulted ? 'fault' : 'not-calculated',
    text: faulted ? CALC_FAULT : notCalculatedText(reason),
    missingInputs: strings(data.MissingInputs).map(inputWords),
  };
}

/** The checklist step the run waits on, or null when it waits on none (another kind of step, or not suspended). */
export function waitingChecklist(
  instance: RunInstance,
  log: readonly RunLogEvent[],
  definition: RunDefinition,
  preferredActivityId?: string | null,
): WaitingChecklist | null {
  const checklists = definition.activities.filter((a) => a.type === CHECKLIST_ACTIVITY_TYPE && isWaiting(instance, log, a.activityId));
  const step = checklists.find((a) => a.activityId === preferredActivityId) ?? checklists[0];
  if (!step) return null;
  return {
    instanceId: instance.id,
    activityId: step.activityId,
    title: step.title || 'Untitled step',
    items: step.items ?? null,
    assigneeIds: step.assigneeIds === undefined ? [] : step.assigneeIds,
    reason: reasonFor(instance, log, definition, step.activityId),
  };
}

/** The sentence under the step title. Never names an engine outcome such as "Error". */
export function reasonLine(reason: ChecklistReason): string {
  if (reason.kind === 'after') return `The run came here after ${reason.afterStep}.`;
  const missing = reason.missingInputs.length > 0 ? ` Missing inputs: ${reason.missingInputs.join(', ')}.` : '';
  return `${reason.afterStep}: ${reason.text}.${missing}`;
}

/* ── Ticking ──────────────────────────────────────────────────────────────── */

export const ASSIGNED_ELSEWHERE = 'This step is assigned to someone else. You can read it but not tick it.';
export const ASSIGNMENT_UNREADABLE = 'Who this step is assigned to could not be read, so it cannot be ticked from here.';
export const MULTI_ITEM_HELD =
  'This step has more than one item. It cannot be ticked until the workflow service checks every item before moving on.';
export const ITEMS_UNREADABLE = 'The items of this step could not be read.';
export const NO_ITEMS = 'This step has no items to tick. The run cannot move on from here: the care model needs fixing in the studio.';
export const ITEM_UNTICKABLE = 'This item cannot be ticked: its name has no text or contains a slash.';

/**
 * Why this user cannot tick the step here, or null when they can. Fails closed:
 *   · an unreadable assignment → read-only;
 *   · an assigned step → only an assignee (the studio stores the doctor setup id, `user.id`; user and practitioner ids
 *     are matched too);
 *   · unreadable items, or a single item the engine cannot tick (see canTickLabel) → nothing to tick.
 * A step with more than one item IS tickable, item by item (each item keeps its own canTickLabel guard in the UI).
 * The 19 Oct engine defect (a tick resumed the step without re-checking the items) is fixed in Scrips.Workflows
 * PROD-3283 (#203): ScripsChecklistActivity re-reads the store on every resume (`AllItemsCompleteAsync`) and
 * suspends again while any item is unticked. The per-item tasks that change raises are on dev (Tasks → Team queue).
 * MULTI_ITEM_HELD is kept exported for a caller that still names it; tickBlock no longer returns it.
 */
export function tickBlock(step: Pick<WaitingChecklist, 'assigneeIds' | 'items'>, me: readonly (string | null | undefined)[]): string | null {
  if (step.assigneeIds === null) return ASSIGNMENT_UNREADABLE;
  if (step.assigneeIds.length > 0) {
    const mine = new Set(me.filter((x): x is string => !!x).map((x) => x.trim().toLowerCase()));
    if (!step.assigneeIds.some((id) => mine.has(id.toLowerCase()))) return ASSIGNED_ELSEWHERE;
  }
  if (step.items === null) return ITEMS_UNREADABLE;
  if (step.items.length > 1) return step.items.some((label) => canTickLabel(label)) ? null : ITEM_UNTICKABLE;
  if (step.items.length === 1 && !canTickLabel(step.items[0])) return ITEM_UNTICKABLE;
  if (step.items.length === 0) return NO_ITEMS;
  return null;
}

/** The slice of Web Storage the tick memory needs, so tests can pass a plain object. */
export interface TickStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const TICK_STORAGE_KEY = 'scrips.care-model-tasks.ticks.v1';
const slot = (instanceId: string, activityId: string) => `${instanceId}|${activityId}`;

function readAllTicks(storage: TickStorage | null): Record<string, string[]> {
  if (!storage) return {};
  try {
    const value = parseText(storage.getItem(TICK_STORAGE_KEY));
    if (!isObject(value)) return {};
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(value)) out[k] = strings(v);
    return out;
  } catch {
    return {};
  }
}

/** The labels ticked from this browser on one run's checklist step. */
export function readTicks(storage: TickStorage | null, instanceId: string, activityId: string): ReadonlySet<string> {
  return new Set(readAllTicks(storage)[slot(instanceId, activityId)] ?? []);
}

/** Record one tick; returns the step's ticked labels after it. A storage failure keeps the tick in memory only. */
export function rememberTick(storage: TickStorage | null, instanceId: string, activityId: string, label: string): ReadonlySet<string> {
  const all = readAllTicks(storage);
  const key = slot(instanceId, activityId);
  const labels = new Set([...(all[key] ?? []), label]);
  all[key] = [...labels];
  try {
    storage?.setItem(TICK_STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* storage blocked: memory only */
  }
  return labels;
}

/**
 * Whether the engine can tick this label. The label travels as one URL path segment and the engine's router does not
 * decode an encoded "/", so such an item would never match while the endpoint still answers success. An empty label
 * cannot travel at all. (Same rule as the studio run view.)
 */
export function canTickLabel(label: string): boolean {
  return label.trim() !== '' && !label.includes('/');
}

/** The tick answered, but did not confirm the item was completed (`data.completed` is not true). */
export class TickUnconfirmedError extends Error {
  constructor() {
    super('checklist tick: completion not confirmed');
    this.name = 'TickUnconfirmedError';
  }
}

/** `{ data: { completed: true } }` (CompleteChecklistItemController) is the only answer that confirms the tick. */
export function tickConfirmed(body: unknown): boolean {
  const d = isObject(body) && 'data' in body ? body.data : body;
  return isObject(d) && d.completed === true;
}

/** A failed tick, in words. Never quotes a body: it may carry patient data. */
export function describeTickError(err: unknown): string {
  if (err instanceof TickUnconfirmedError) return 'The workflow service did not confirm the item. The run was read again: check it before trying again.';
  const status = typeof err === 'object' && err !== null ? (err as { status?: unknown }).status : undefined;
  if (typeof status !== 'number') return 'The item could not be marked done.';
  if (status === 0) return 'The workflow service could not be reached. Try again.';
  if (status === 401) return 'Your session has expired. Sign in again, then retry.';
  if (status === 403) return 'Your sign-in is not allowed to tick items on this run.';
  if (status === 404) return 'This checklist was not found in your organisation.';
  if (status === 400 || status === 422) return 'The workflow service refused this item.';
  return `The workflow service could not mark the item done (HTTP ${status}).`;
}
